import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  getAnalyticsOverview,
  getAnalyticsTimeseries,
  getAnalyticsFunnel,
  getAnalyticsSources,
  type AnalyticsTimeseriesMetric,
  type AnalyticsSourceDimension,
} from '@/services'
import { AppButton, EmptyState, KPICard, Skeleton } from '@/components/platform/primitives'
import { Funnel, TimeSeriesChart } from '@/components/platform/charts'
import { formatCurrency } from '@/lib/utils'

const RANGES: Array<{ id: string; label: string; days: number }> = [
  { id: 'hoy', label: 'hoy', days: 1 },
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
]

const METRIC_LABELS: Record<AnalyticsTimeseriesMetric, string> = {
  sessions: 'Sesiones',
  visitors: 'Visitantes',
  conversions: 'Conversiones',
}

const DIMENSIONS: Array<[AnalyticsSourceDimension, string]> = [
  ['utm_source', 'UTM source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['referrer', 'referrer'],
]

const VALID_METRICS = Object.keys(METRIC_LABELS) as AnalyticsTimeseriesMetric[]
const VALID_DIMENSIONS = DIMENSIONS.map(([key]) => key)

// El reloj se lee cuando se dispara el pedido, no mientras se renderiza: asi la
// ventana que se manda es la del momento del fetch y el render no depende de la
// hora. Antes `to` cambiaba en cada render mientras la queryKey solo miraba el
// rango, con lo cual el rango pedido y el cacheado podian no coincidir.
function rangeFor(days: number): { from: string; to: string } {
  const now = Date.now()
  return {
    from: new Date(now - days * 86_400_000).toISOString(),
    to: new Date(now).toISOString(),
  }
}

export function WebAnalyticsPage() {
  // Estado de filtros en la URL: deep-linkeable, sobrevive recarga y back/forward.
  const [params, setParams] = useSearchParams()
  const rangeId = RANGES.some((r) => r.id === params.get('range')) ? params.get('range')! : '30d'
  const metric = (VALID_METRICS.includes(params.get('metric') as AnalyticsTimeseriesMetric) ? params.get('metric') : 'sessions') as AnalyticsTimeseriesMetric
  const dimension = (VALID_DIMENSIONS.includes(params.get('dimension') as AnalyticsSourceDimension) ? params.get('dimension') : 'utm_source') as AnalyticsSourceDimension
  const setParam = (key: string, value: string) =>
    setParams((prev) => { const p = new URLSearchParams(prev); p.set(key, value); return p }, { replace: true })

  const days = RANGES.find((r) => r.id === rangeId)?.days || 30

  const { data: overview, isLoading: overviewLoading } = useQuery({ queryKey: ['wa-overview', rangeId], queryFn: () => getAnalyticsOverview(rangeFor(days)) })
  const { data: series, isLoading: seriesLoading } = useQuery({ queryKey: ['wa-timeseries', rangeId, metric], queryFn: () => getAnalyticsTimeseries({ ...rangeFor(days), metric }) })
  const { data: funnel, isLoading: funnelLoading } = useQuery({ queryKey: ['wa-funnel', rangeId], queryFn: () => getAnalyticsFunnel(rangeFor(days)) })
  const { data: sources, isLoading: sourcesLoading } = useQuery({ queryKey: ['wa-sources', rangeId, dimension], queryFn: () => getAnalyticsSources({ ...rangeFor(days), dimension }) })

  const seriesPoints = (series?.data || []).map((p) => ({
    label: new Date(p.day).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }),
    value: p.value,
  }))
  const sourceRows = sources?.data || []
  const maxSessions = Math.max(...sourceRows.map((r) => r.sessions), 1)
  const funnelSteps = (funnel?.data || []).map((s) => ({ key: s.key, label: s.label, count: s.count }))

  const exportSourcesCsv = () => {
    const header = ['fuente', 'sesiones', 'visitantes', 'conversiones', 'tasa_conversion']
    const rows = sourceRows.map((r) => [String(r[dimension] ?? '(directo)'), r.sessions, r.visitors, r.conversions, r.conversion_rate])
    const csv = [header, ...rows]
      .map((cols) => cols.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(','))
      .join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `web-analytics-fuentes-${dimension}-${rangeId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Marketing</div>
          <h1>Web Analytics</h1>
          <div className="subtitle">Tráfico de bisne.app · {rangeId === 'hoy' ? 'hoy' : rangeId.replace('d', ' días')} · datos en tiempo real</div>
        </div>
        <div className="page-actions">
          <div className="metric-toggle">
            {RANGES.map((r) => (
              <button key={r.id} className={rangeId === r.id ? 'active' : ''} aria-pressed={rangeId === r.id} onClick={() => setParam('range', r.id)} type="button">{r.label}</button>
            ))}
          </div>
          <AppButton variant="secondary" icon="download" onClick={exportSourcesCsv} disabled={sourceRows.length === 0}>Exportar</AppButton>
        </div>
      </div>

      <div className="kpi-grid">
        <KPICard label="Sesiones" icon="activity" loading={overviewLoading} value={(overview?.sessions || 0).toLocaleString('es-AR')} feature />
        <KPICard label="Visitantes únicos" icon="users" loading={overviewLoading} value={(overview?.visitors || 0).toLocaleString('es-AR')} />
        <KPICard label="Conversiones" icon="target" loading={overviewLoading} value={String(overview?.conversions || 0)} />
        <KPICard label="Tasa de conversión" icon="trending" loading={overviewLoading} value={String(overview?.conversion_rate ?? 0)} unit="%" />
        <KPICard label="Valor de conversión" icon="banknote" loading={overviewLoading} value={formatCurrency(overview?.conversion_value || 0, 'ARS')} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginTop: 16 }}>
        <div className="card">
          <div className="card-header">
            <div>
              <h3>{METRIC_LABELS[metric]} por día</h3>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Últimos {days} días</div>
            </div>
            <div className="metric-toggle">
              {(Object.keys(METRIC_LABELS) as AnalyticsTimeseriesMetric[]).map((m) => (
                <button key={m} className={metric === m ? 'active' : ''} aria-pressed={metric === m} onClick={() => setParam('metric', m)} type="button">{METRIC_LABELS[m]}</button>
              ))}
            </div>
          </div>
          <div className="card-body">
            {seriesLoading
              ? <Skeleton height={220} />
              : seriesPoints.length === 0
                ? <EmptyState icon="activity" title="Todavía no hay datos de tráfico en este rango" desc="Cuando empiecen a llegar visitas a bisne.app, vas a verlas acá." />
                : <TimeSeriesChart data={seriesPoints} />}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div>
              <h3>Embudo de conversión</h3>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Visitante → Pago</div>
            </div>
          </div>
          <div className="card-body">
            {funnelLoading
              ? <Skeleton height={220} />
              : funnelSteps.length === 0
                ? <EmptyState icon="funnel" title="Sin eventos para construir el embudo" desc="Necesitamos al menos un puñado de sesiones para empezar a medir caídas." />
                : <Funnel steps={funnelSteps} />}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div>
            <h3>Fuentes de tráfico</h3>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>¿Qué orígenes traen gente que efectivamente paga?</div>
          </div>
          <div className="row gap-2">
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Dimensión</span>
            <div className="metric-toggle">
              {DIMENSIONS.map(([k, lbl]) => (
                <button key={k} className={dimension === k ? 'active' : ''} aria-pressed={dimension === k} onClick={() => setParam('dimension', k)} type="button">{lbl}</button>
              ))}
            </div>
          </div>
        </div>
        {sourcesLoading ? (
          <div className="card-body"><Skeleton height={180} /></div>
        ) : sourceRows.length === 0 ? (
          <EmptyState icon="globe" title="Sin fuentes de tráfico para mostrar" desc="Las campañas vacías o sin UTMs todavía no aparecen acá." />
        ) : (
          <div className="card-body flush">
            <table className="table">
              <thead>
                <tr>
                  <th>Fuente</th>
                  <th style={{ textAlign: 'right' }}>Sesiones</th>
                  <th style={{ textAlign: 'right' }}>Visitantes</th>
                  <th style={{ textAlign: 'right' }}>Conversiones</th>
                  <th style={{ textAlign: 'right', minWidth: 110 }}>Tasa conv.</th>
                  <th style={{ width: 160 }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {sourceRows.map((row, i) => {
                  const label = String(row[dimension] ?? '(directo)')
                  const cr = row.conversion_rate
                  const sharePct = ((row.sessions / maxSessions) * 100).toFixed(0)
                  return (
                    <tr key={`${label}-${i}`}>
                      <td className="cell-primary"><span className="code-chip" style={{ fontSize: 12 }}>{label}</span></td>
                      <td style={{ textAlign: 'right' }} className="tabular">{row.sessions.toLocaleString('es-AR')}</td>
                      <td style={{ textAlign: 'right' }} className="tabular">{row.visitors.toLocaleString('es-AR')}</td>
                      <td style={{ textAlign: 'right' }} className="tabular">{row.conversions}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: cr > 2 ? 'var(--success-fg)' : cr > 1 ? 'var(--text)' : 'var(--text-3)' }}>{cr}%</td>
                      <td>
                        <div className="track" style={{ background: 'var(--surface-3)', height: 8, borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ background: 'var(--bisne-green)', height: '100%', width: `${sharePct}%`, borderRadius: 999 }} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
